import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ArrowLeft, Package, Plus, Trash2, ChevronRight, Camera, DollarSign, Calendar, Search } from 'lucide-react';
import { toast } from 'sonner';

const CATEGORIES = [
  'Elektronik',
  'Möbler', 
  'Kök',
  'Kläder & Accessoarer',
  'Sport & Fritid',
  'Verktyg',
  'Konst & Dekoration',
  'Fordon',
  'Övrigt'
];

export default function InventoryPage() {
  const navigate = useNavigate();
  const { hasAccess } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('Alla');
  const [newItem, setNewItem] = useState({
    name: '',
    category: 'Elektronik',
    purchaseDate: '',
    purchasePrice: '',
    warranty: '',
    notes: ''
  });

  useEffect(() => {
    if (hasAccess) {
      const saved = localStorage.getItem('inventory-items');
      if (saved) {
        setItems(JSON.parse(saved));
      }
      setLoading(false);
    } else {
      setLoading(false);
    }
  }, [hasAccess]);

  const saveItems = (updated) => {
    setItems(updated);
    localStorage.setItem('inventory-items', JSON.stringify(updated));
  };

  const addItem = () => {
    if (!newItem.name.trim()) {
      toast.error('Ange ett namn');
      return;
    }
    const item = {
      id: Date.now(),
      ...newItem,
      purchasePrice: newItem.purchasePrice ? Number(newItem.purchasePrice) : null,
      createdAt: new Date().toISOString()
    };
    saveItems([...items, item]);
    setNewItem({
      name: '',
      category: 'Elektronik',
      purchaseDate: '',
      purchasePrice: '',
      warranty: '',
      notes: ''
    });
    setShowForm(false);
    toast.success('Sak tillagd!');
  };

  const deleteItem = (itemId) => {
    saveItems(items.filter(i => i.id !== itemId));
    toast.success('Sak borttagen');
  };

  const filteredItems = items.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'Alla' || item.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const totalValue = items.reduce((sum, item) => sum + (item.purchasePrice || 0), 0);

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-background" data-testid="inventory-page">
        <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/category/home')} className="rounded-full">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="font-heading text-xl font-light">Inventarier</h1>
          </div>
        </header>
        <main className="max-w-4xl mx-auto px-6 py-12 text-center">
          <p className="text-muted-foreground">Uppgradera för att få tillgång till inventarier.</p>
          <Button onClick={() => navigate('/subscription')} className="rounded-full mt-4">Uppgradera nu</Button>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="inventory-page">
      <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/category/home')} className="rounded-full">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="font-heading text-xl font-light">Inventarier</h1>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        <div className="text-center space-y-2">
          <Package className="w-12 h-12 mx-auto text-amber-500" />
          <h2 className="font-heading text-2xl font-semibold">Inventarier</h2>
          <p className="text-muted-foreground">Håll koll på värdefulla saker i hemmet</p>
        </div>

        {/* Summary */}
        <div className="bg-amber-50 rounded-2xl p-4 border border-amber-100">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-sm text-amber-700">Totalt värde</p>
              <p className="text-2xl font-bold text-amber-800">{totalValue.toLocaleString('sv-SE')} kr</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-amber-700">Antal saker</p>
              <p className="text-2xl font-bold text-amber-800">{items.length}</p>
            </div>
          </div>
        </div>

        {/* Search and filter */}
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Sök..."
              className="pl-10"
            />
          </div>
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="px-3 py-2 border rounded-lg text-sm"
          >
            <option value="Alla">Alla kategorier</option>
            {CATEGORIES.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
          <Button onClick={() => setShowForm(true)} className="rounded-xl px-4" data-testid="add-inventory-btn">
            <Plus className="w-4 h-4 mr-2" />
            Lägg till
          </Button>
        </div>

        {/* Add form */}
        {showForm && (
          <div className="bg-white rounded-2xl p-6 border border-border/30 space-y-4">
            <h3 className="font-semibold">Lägg till sak</h3>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="text-sm text-muted-foreground">Namn *</label>
                <Input
                  value={newItem.name}
                  onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                  placeholder="T.ex. Samsung TV 55 tum"
                />
              </div>
              <div>
                <label className="text-sm text-muted-foreground">Kategori</label>
                <select
                  value={newItem.category}
                  onChange={(e) => setNewItem({ ...newItem, category: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  {CATEGORIES.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm text-muted-foreground">Inköpsdatum</label>
                <Input
                  type="date"
                  value={newItem.purchaseDate}
                  onChange={(e) => setNewItem({ ...newItem, purchaseDate: e.target.value })}
                />
              </div>
              <div>
                <label className="text-sm text-muted-foreground">Inköpspris (kr)</label>
                <Input
                  type="number"
                  value={newItem.purchasePrice}
                  onChange={(e) => setNewItem({ ...newItem, purchasePrice: e.target.value })}
                  placeholder="T.ex. 12999"
                />
              </div>
              <div>
                <label className="text-sm text-muted-foreground">Garanti (utgår)</label>
                <Input
                  type="date"
                  value={newItem.warranty}
                  onChange={(e) => setNewItem({ ...newItem, warranty: e.target.value })}
                />
              </div>
              <div>
                <label className="text-sm text-muted-foreground">Anteckningar</label>
                <Input
                  value={newItem.notes}
                  onChange={(e) => setNewItem({ ...newItem, notes: e.target.value })}
                  placeholder="Serienummer, plats, etc."
                />
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowForm(false)} className="rounded-full">
                Avbryt
              </Button>
              <Button onClick={addItem} className="rounded-full">
                Lägg till
              </Button>
            </div>
          </div>
        )}

        {/* Items list */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-amber-200 border-t-amber-500 rounded-full animate-spin" />
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">
              {items.length === 0 ? 'Inga saker än. Lägg till din första!' : 'Inga saker matchar sökningen.'}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {filteredItems.map(item => (
              <div
                key={item.id}
                className="bg-white p-4 rounded-xl border border-border/30 flex items-center gap-4"
              >
                <div className="w-12 h-12 rounded-xl bg-amber-100 text-amber-600 flex items-center justify-center">
                  <Package className="w-6 h-6" />
                </div>
                <div className="flex-1">
                  <h4 className="font-medium">{item.name}</h4>
                  <div className="flex flex-wrap gap-3 text-xs text-muted-foreground mt-1">
                    <span className="bg-gray-100 px-2 py-0.5 rounded">{item.category}</span>
                    {item.purchasePrice && (
                      <span className="flex items-center gap-1">
                        <DollarSign className="w-3 h-3" />
                        {item.purchasePrice.toLocaleString('sv-SE')} kr
                      </span>
                    )}
                    {item.purchaseDate && (
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {new Date(item.purchaseDate).toLocaleDateString('sv-SE')}
                      </span>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => deleteItem(item.id)}
                  className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Tips */}
        <div className="bg-amber-50 rounded-2xl p-4 border border-amber-100">
          <p className="text-amber-800 text-sm">
            💡 <strong>Tips:</strong> Ta bilder på kvitton och serienummer för försäkringsändamål!
          </p>
        </div>
      </main>
    </div>
  );
}
